<template>
	<div class="relative">
		<div class="fixed white" style="width: 100%;" id="tool-bar">
			<v-row
				v-if="$route.name === 'custom-statement'"
				class="ma-0"
				style="width: 100%"
				align="center"
				justify="start"
			>
				<v-col cols="auto" class="d-flex flex-column justify-start">
					<slot name="breadcrumbs" :item="true"></slot>
				</v-col>
				<v-spacer></v-spacer>
				<!-- export -->
				<v-col cols="auto" class="text-sm-start text-center">
					<v-menu offset-y>
						<template v-slot:activator="{ on }">
							<v-btn dark color="printingColor" class="px-2" v-on="on">
								<span class="d-flex align-center">
									<v-icon color="white">mdi-export-variant</v-icon>
									<span style="font-size: 14px">
										{{$t('AccountCustomStatementPage.export')}}
									</span>
								</span>
							</v-btn>
						</template>

						<bee-list>
							<!-- excel -->
							<bee-list-item
								context="custom_statement"
								:item="exportItems[0]"
								dense
								@click="exportAs('excel')"
							></bee-list-item>

							<!-- pdf -->
							<bee-list-item
								context="custom_statement"
								:item="exportItems[1]"
								dense
								@click="exportAs('pdf')"
							></bee-list-item>
				
						</bee-list>
					</v-menu>
				</v-col>
			</v-row>
			<v-layout wrap class="d-flex flex-row my-border rounded-lg mx-3 mb-3 align-center">
				<!-- custom statement -->
				<v-col cols="auto" lg="3" class="d-flex flex-column">
					<bee-autocomplete
						v-model="custom_statement_id"
						:label="$t('AccountCustomStatementPage.custom-statements')"
						:items="!isStatisticsView ? custom_statements : customStatements"
						item-text="name"
						item-value="id"
						ref="BeeAutocomplete"
						:hide-details="true"
						@keydown-enter-prevent="search()"
						context="custom_statement"
						:action-name="actions.GET"
					></bee-autocomplete>
				</v-col>

				<v-col cols="auto" lg="auto" md="auto" sm="6" xs="12" class="d-flex flex-column">
					<bee-checkbox
						v-model="show_non_zero_accounts_only"
						:label="$t('AccountCustomStatementPage.balanced-accounts-only')"
						class="small-font-size mt-0" 
					></bee-checkbox>
				</v-col>

				<!-- show all columns -->
				<v-col cols="auto" lg="auto" md="auto" sm="6" class="d-flex px-lg-0 flex-column">
					<bee-checkbox
						v-model="show_all_columns"
						:label="$t('AccountCustomStatementPage.show-all-columns')"
						class="small-font-size mt-0"
					></bee-checkbox>

				</v-col>
				<v-spacer v-if="!isStatisticsView"></v-spacer>
				<!-- search -->
				<v-col cols="auto" lg="auto" md="auto" sm="3" class="d-flex justify-end">
					<bee-btn  
						@click="search()"
						:content="$t('AccountCustomStatementPage.refresh')"
						color="blueGray"
						outer-class="mt-0 me-3"
						:loading="loading"
						context="custom_statement" 
						:action-name="actions.GET"
					></bee-btn>
					<v-icon small color="grey" @click="reset()" :loading="loading">fa-times-circle</v-icon>
				</v-col>
			</v-layout>
		</div>
		<div class="ma-auto absolute pb-0" id="main" style="width: 100%; height: auto;">
			<v-card flat>
				<v-card-text class="pt-0 px-3">
					<!-- main table -->
					<bee-handy-table
						:headers="headers"
						:items="!isStatisticsView ? custom_statement_rows : customStatementsData.custom_statement_rows"
						:height="$vuetify.breakpoint.height - 310"
						:data-table-props="{ disableSort: true }"
						:item-class="item => {
							const account = getAccountById(item.account_id)
							if(account.type === type.main)
								return 'orangeLighten5'
						}"
						fixed-header
						with-columns-divider
						zebra
						pagination-on-scroll
						shown-rows-text=""
						:context-menu-items="contextMenuItems"
						@contextmenu:row="(e, data) => contextMenuItem = data.item"
						@context-menu-statement="statement($event.item)"
						@dblclick:row="(e, item) => statement(item)"
					>
						<template v-slot:item.options="{ item }">
							<div class="text-center">
								<v-menu offset-y>
									<template v-slot:activator="{ on, attr }">
										<v-btn
											small
											icon
											v-bind="attr"
											v-on="on"
											width="32"
										>
											<v-icon>
												mdi-dots-vertical
											</v-icon>
										</v-btn>
									</template>
									<v-list dense>
										<v-list-item @click="statement(item)">
											<v-list-item-content class="pa-0">
												{{ $t('menus.account-statement') }}
											</v-list-item-content>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</template>
					</bee-handy-table>
					
				</v-card-text>
			</v-card>
		</div>

		<!-- footer -->
		<v-bottom-navigation id="footer" app horizontal class="px-3" background-color="#E1E2E4">
			<v-row align="center" justify="center" class="flex-wrap">
				<v-spacer></v-spacer>
				<!-- debit balance -->
				<v-col cols="auto" lg="auto" class="d-flex flex-column">
					<bee-outlined-header-info
					    border-color="transparent"
					    :head="$t('AccountCustomStatementPage.total-debit-balance')"
					>
						<template v-slot:content="{}">
							<bee-advanced-money-label
								empty-state="_"
								v-model="debit_balance"
								:bold="true"
								:with-color="true"
								:currencies="currencies"
							></bee-advanced-money-label>
						</template>
					</bee-outlined-header-info>
				</v-col>

				<!-- credit balance -->
				<v-col cols="auto" lg="auto" class="d-flex flex-column">
					<bee-outlined-header-info
					    border-color="transparent"
						:head="$t('AccountCustomStatementPage.total-credit-balance')"
					>
						<template v-slot:content="{}">
							<bee-advanced-money-label
								empty-state="_"
								v-model="credit_balance"
								:bold="true"
								:with-color="true"
								:currencies="currencies"
							></bee-advanced-money-label>
						</template>
					</bee-outlined-header-info>
				</v-col>
				<!-- difference balance-->
				<v-col cols="auto" lg="auto" class="d-flex flex-column">
					<bee-outlined-header-info
					    border-color="transparent"
						:head="$t('AccountCustomStatementPage.total-difference-balance')"
					>
						<template v-slot:content="{}">
							<bee-advanced-money-label
								empty-state="_"
								v-model="difference_balance"
								:bold="true"
								:with-color="true"
								:currencies="currencies"
							></bee-advanced-money-label>
						</template>
					</bee-outlined-header-info>
				</v-col>
			</v-row>
		</v-bottom-navigation>
	</div>
</template>
<script>
import store from "../../../store/index.js";
import { mapGetters, mapState } from "vuex";
import actions from "../../../permissions/actions-names.js";
import ls from "../../../settings/localStorageExtension";
import { type } from '../../../helpers/enums.js'
export default {
	data: () => ({
		actions,
		custom_statement_id: null,
		show_all_columns: false,
		show_non_zero_accounts_only: false,
		obj: {},
		loading: false,
		type,
		customStatements: [],
		customStatementsData: {}
	}),

	watch: {
		'$route.query.financialFileId': {
			handler() {
				if (this.isStatisticsView && this.$route.query.financialFileId) {
					this.$store.dispatch('accounts/fetchAll', {
						financialFileId: this.$route.query.financialFileId,
					});
					this.$store.dispatch('customStatements/fetchAll', {
						financialFileId: this.$route.query.financialFileId
					}).then(data => {
						this.customStatements = data;
					});
				}
			},
			immediate: true
		},
	},

	computed: {
		exportItems() {
			return [
				{
					text: this.$t('AccountCustomStatementPage.export-as-excel'),
					icon: 'mdi-microsoft-excel',
					context: 'custom_statement',
					action_name: actions.EXPORT_AS_PDF
				},
				{
					text: this.$t('AccountCustomStatementPage.print-the-account-custom-statement'),
					icon: 'mdi-printer',
					context: 'custom_statement',
					action_name: actions.EXPORT_AS_PDF
				},
			]
		},

		// tables headers
		headers() {
			let headers = [
				{
					text: this.$t('AccountCustomStatementPage.headers.account-no'),
					name: 'accountNo',
					value: 'account_id',
					setValueExpr: value => this.getAccountById(value)?.accounting_no,
					width: "6%",
				},
				{
					text: this.$t('AccountCustomStatementPage.headers.account-name'),
					name: 'accountName',
					value: 'account_id',
					setValueExpr: value => this.getAccountById(value)?.name,
					width: "14%",
				},
				{
					text: this.$t('AccountCustomStatementPage.headers.parent-account'),
					name: 'parentAccountId',
					value: 'parent_account_id',
					noNotAvailableText: true,
					setValueExpr: value => {
						if(value) {
							let account = this.getAccountById(value);
							while(account.parent_account_id) {
								account = this.getAccountById(account.parent_account_id);
							}
							return account.accounting_no + '-' + account.name;
						} else {
							return '-'
						}
					},
					width: "14%"
				},
				{
					text: this.$t('AccountCustomStatementPage.headers.debit-balance'),
					name: 'debitBalance',
					value: "debit_balance.amount",
					type: 'number-format',
					width: "13%",
				},
				{
					text: this.$t('AccountCustomStatementPage.headers.credit-balance'),
					name: 'creditBalance',
					value: "credit_balance.amount",
					type: 'number-format',
					width: "13%",
				},
				
				...(
					this.show_all_columns 
					? [
						{
							text: this.$t('AccountCustomStatementPage.headers.last-debit-amount'),
							value: "last_debit_amount.amount",
							type: "number-format",
							width: "10%",
						},
						{
							text: this.$t('AccountCustomStatementPage.headers.last-debit-date'),
							value: "last_debit_date",
							type: "date",
							width: "10%"
						},
						{
							text: this.$t('AccountCustomStatementPage.headers.last-credit-amount'),
							value: "last_credit_amount.amount",
							type: "number-format",
							width: "10%",
						},
						{
							text: this.$t('AccountCustomStatementPage.headers.last-credit-date'),
							value: "last_credit_date",
							type: "date",
							width: "10%"
						}
					]
					: []
				),

				{
                    text: '',
                    name: 'options',
                    value: 'options',
                    width: '1%',
					divider: false,
                    class: 'px-0',
                    cellClass: 'px-0'
                }
			];
			headers = headers.filter(c => Boolean(c))

			return headers
		},

		contextMenuItems() {
			const contextMenuItems = [
				{
					title: this.$t('menus.account-statement'),
					event: 'statement'
				}
			];
			return contextMenuItems
		},

		custom_statement_rows() {
			return this.account_custom_statement?.custom_statement_rows || [];
		},
		custom_statement_summary() {
			const data = !this.isStatisticsView ? this.account_custom_statement : this.customStatementsData;
			return data?.custom_statement_summary ? [].concat(data.custom_statement_summary) : [];
		},

		file_is_locked() {
			return this.configs?.financial_file?.is_locked;
		},

		showHeaders() {
			return this.show_all_columns ? this.headers : this.subHeaders;
		},

		currencies() {
			return !this.isStatisticsView
				? this.configs.currencies
				: this.allCurrencies.map(c => ({
					...c,
					is_main_currency:
						c.id === this.getFinancialFileById(+this.$route.query.financialFileId).default_currency_id
				}))
		},

		// footer
		debit_balance(){
			return this.custom_statement_summary[0]?.debit_balance;
		},
		credit_balance(){
			return this.custom_statement_summary[0]?.credit_balance;
		},
		difference_balance(){
			return this.custom_statement_summary[0]?.sum_difference;
		},
		...mapState({
			configs: state => state.configs.configs,
			account_custom_statement: state => state.statements.account_custom_statement,
			custom_statements: state => state.customStatements.custom_statements,
			accounts: state => state.accounts.accounts,
			allCurrencies: state => state.currencies.currencies,
		}),

		...mapGetters({
			getFinancialFileById: 'financialFiles/getFinancialFileById',
			getAccountById: 'configs/getAccountById',
		}),

		isStatisticsView() {
			return this.$route.name.includes('statistics');
		}
	},

	methods: {
		test(v){console.log(v)},
		changeSumDifferenceEquivalent(toCurrencyId) {
			if (toCurrencyId) {
				let sum_difference = this.account_custom_statement
					.custom_statement_summary.sum_difference;
				if (sum_difference?.amount) {
					let amount = sum_difference.amount;
					let fromCurrecyId = sum_difference.currency_id;
					store
						.dispatch("currencies/exchangeToCurrency", {
							toCurrencyId,
							amount,
							fromCurrecyId
						})
						.then(response => {
							_.set(
								this.account_custom_statement.custom_statement_summary
									.sum_difference_equivalent,
								"amount",
								response.amount
							);
							_.set(
								this.account_custom_statement.custom_statement_summary
									.sum_difference_equivalent,
								"currency_id",
								response.currency_id
							);
						});
				}
			}
		},
		search(){
			if (this.custom_statement_id) {
				let promise;
				if (!this.isStatisticsView) {
					promise = this.$store.dispatch("statements/fetchAccountCustomStatement", {
						custom_statement_id: this.custom_statement_id,
						show_non_zero_accounts_only : this.show_non_zero_accounts_only
					}).then(() => {
						this.$router.push({
							name: "custom-statement",
							params: { id: this.custom_statement_id }
						}).catch(() => {});
					})
				} else {
					promise = this.$store.dispatch('statistics/fetchAccountCustomStatement', {
						customStatementId: this.custom_statement_id,
						financialFileId: this.$route.query.financialFileId,
						userId: ls.getLocalUser()?.id
					}).then(data => {
						this.customStatementsData = data;
					});
				}
				
				this.loading = true;
				promise.finally(() => {
					this.loading = false;
				})
			}
		},
		exportAs(type) {
			this.$store.dispatch('reports/CustomAccountStatement', {
				type,
				financialFileId: this.configs.financial_file.id,
				customStatementId: this.custom_statement_id,
				userId: ls.getLocalUser().id,
				showNonZeroAccountsOnly: this.show_non_zero_accounts_only
			})
		},
		reset() {
			if (this.isStatisticsView) {
				this.$router.push({ name: "account-custom-statement" })
					.then(() => store.dispatch("statements/clear"))
					.catch(() => {});
			}
			this.custom_statement_id = null;
			this.show_non_zero_accounts_only = null;

			this.setFocus();
		},
		setFocus() {
			this.$refs["BeeAutocomplete"].$refs.BeeAutocomplete.focus();
		},
		statement(item) {
			let routeData = this.$router.resolve({ name: 'statement', params: {id: item.account_id} });
			window.open(routeData.href, '_blank');
		},
	},

	mounted() {
		window.observer.observe(document.querySelector("#tool-bar"))
		window.observer.observe(document.querySelector("#footer"))
		
		if (this.$route.params.id && !this.isStatisticsView) {
			this.custom_statement_id = Number(this.$route.params.id);
			this.search()
		}

		if (this.isStatisticsView && !this.allCurrencies.length) {
			this.$store.dispatch('currencies/fetchAll')
		}

		this.setFocus();
	},

	async beforeRouteEnter(to, from, next) {
		next();
	}
};
</script>
<style scoped>
.small {
	max-width: 600px;
	margin: 150px auto;
}
.small-font-size .v-label {
	font-size: 0.8rem !important;
	padding-right: 0px !important;
}
.my-border {
	border: 1px solid !important;
	border-color: #f1f1f1 !important;
}
</style>
